import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Save, Send, Upload, Sparkles, Check, X as XIcon, Image as ImageIcon, Film, Wand2, Loader2 } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  CHALLENGE_CATEGORIES,
  INDUSTRIES,
  COMPANY_SIZES,
  OUTCOME_CATEGORIES,
  TRUE_COLORS_SOLUTIONS,
  CONSENT_OPTIONS,
  PROOF_POINTS,
  ARCHETYPES,
  COUNTRIES,
  US_STATES,
} from "../lib/constants";
import RewardOverlay from "../components/RewardOverlay";
import { cjReact } from "../lib/cjCelebrate";

// ----- step config -----
const STEPS = [
  { key: "industry", title: "What sector were they in?", subtitle: "Pick a sector. This helps your teammates find similar stories." },
  { key: "company_name", title: "Which company is this about?", subtitle: "Even if they're staying anonymous publicly, we need to know who the story is about internally." },
  { key: "company_size", title: "How big is the team?", subtitle: "Roughly how many people work there?" },
  { key: "challenge", title: "What was the challenge?", subtitle: "Briefly capture what the organization's pain points were before they reached out to TC." },
  { key: "trigger", title: "What pushed them to act?", subtitle: "A board meeting? A failed launch? A walkout?" },
  { key: "solution", title: "Which True Colors programs were experienced?", subtitle: "Tap all that apply. You can pick more than one." },
  { key: "engagement", title: "How was it delivered?", subtitle: "Quick optional details — people trained, format, when, what kind. These feed Robert's CEO thank-you email." },
  { key: "after", title: "What changed after?", subtitle: "Tell the 'after' story. The tighter, the better." },
  { key: "outcomes", title: "Proof points", subtitle: "What told you it worked? Tap anything that applies. Adds +50." },
  { key: "quote", title: "Any standout client quote?", subtitle: "A pull-quote earns +25, and lands hard in pitches." },
  { key: "media", title: "Photo or short video?", subtitle: "Optional but powerful. +25 if you add media." },
  { key: "consent", title: "Who can we share this with?", subtitle: "Respect client consent. We'll only publish what you confirm." },
  { key: "tags", title: "Tag it for findability", subtitle: "Helps the team pull this up in seconds." },
];

const empty = {
  industry: "",
  company_name: "",
  company_size: "",
  challenge_text: "",
  challenge_categories: [],
  trigger_to_act: "",
  true_colors_solution: [],
  after_text: "",
  proof_points: [],
  client_quote: "",
  consent_status: "",
  follow_up_reminder: false,
  facilitator: "",
  state: "",
  country: "",
  people_trained: "",
  training_date: "",
  delivery_format: "",
  engagement_type: "",
  client_contact_name: "",
  client_contact_email: "",
  title: "",
  tags: { industry: "", company_size: "", challenge_type: "", outcome_category: "" },
  client_id: "",
};

export default function Capture() {
  const { id: storyIdParam } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [storyId, setStoryId] = useState(storyIdParam || null);
  const [step, setStep] = useState(0);
  const [data, setData] = useState(empty);
  const [media, setMedia] = useState([]);
  const [clientLogoPath, setClientLogoPath] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [strength, setStrength] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [reward, setReward] = useState(null);
  const fileInputRef = useRef(null);
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);

  // Load existing draft if editing
  useEffect(() => {
    if (!storyIdParam) return;
    api.get(`/stories/${storyIdParam}`).then(({ data: s }) => {
      setData({
        title: s.title || "",
        industry: s.industry || "",
        company_name: s.company_name || s.client_submitter_org || "",
        company_size: s.company_size || "",
        challenge_text: s.challenge_text || "",
        challenge_categories: s.challenge_categories || [],
        trigger_to_act: s.trigger_to_act || "",
        true_colors_solution: Array.isArray(s.true_colors_solution) ? s.true_colors_solution : (s.true_colors_solution ? [s.true_colors_solution] : []),
        after_text: s.after_text || "",
        proof_points: s.proof_points || [],
        client_quote: s.client_quote || "",
        consent_status: s.consent_status || "",
        follow_up_reminder: !!s.follow_up_reminder,
        facilitator: s.facilitator || "",
        state: s.state || "",
        country: s.country || "",
        people_trained: s.people_trained || "",
        training_date: s.training_date || "",
        delivery_format: s.delivery_format || "",
        engagement_type: s.engagement_type || "",
        client_contact_name: s.client_contact_name || "",
        client_contact_email: s.client_contact_email || "",
        tags: s.tags || empty.tags,
        client_id: s.client_id || "",
      });
      setMedia(s.media || []);
      setClientLogoPath(s.client_logo_path || "");
    });
  }, [storyIdParam]);

  // Create a draft on first edit if none exists yet
  const ensureDraft = async () => {
    if (storyId) return storyId;
    const { data: created } = await api.post("/stories", { industry: data.industry || "" });
    setStoryId(created.id);
    window.history.replaceState(null, "", `/capture/${created.id}`);
    return created.id;
  };

  const saveProgress = async () => {
    setSaving(true);
    try {
      const sid = await ensureDraft();
      // Strip values the backend's strict Pydantic types reject (empty string
      // is not a valid Literal value for consent_status).
      const cleaned = { ...data };
      if (!cleaned.consent_status) delete cleaned.consent_status;
      const payload = {
        ...cleaned,
        facilitator: data.facilitator || undefined,
        state: data.state || undefined,
        country: data.country || undefined,
        people_trained: data.people_trained || undefined,
        training_date: data.training_date || undefined,
        delivery_format: data.delivery_format || undefined,
        engagement_type: data.engagement_type || undefined,
        client_contact_name: data.client_contact_name || undefined,
        client_contact_email: data.client_contact_email || undefined,
        tags: {
          industry: data.tags?.industry || data.industry || undefined,
          company_size: data.tags?.company_size || data.company_size || undefined,
          challenge_type: data.tags?.challenge_type || (data.challenge_categories?.[0] || undefined),
          outcome_category: data.tags?.outcome_category || undefined,
        },
      };
      await api.put(`/stories/${sid}`, payload);
    } finally {
      setSaving(false);
    }
  };

  // Autosave on step change (debounced via per-action save)
  const next = async () => {
    await saveProgress();
    setStep((s) => Math.min(STEPS.length, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const handleMediaUpload = async (file) => {
    setUploadError("");
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setUploadError("File is too large (max 25MB).");
      return;
    }
    setUploading(true);
    try {
      const sid = await ensureDraft();
      const fd = new FormData();
      fd.append("file", file);
      const { data: asset } = await api.post(`/stories/${sid}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMedia((m) => [...m, asset]);
    } catch (e) {
      setUploadError(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleLogoUpload = async (file) => {
    setUploadError("");
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setUploadError("Logo is too large (max 25MB).");
      return;
    }
    setUploadingLogo(true);
    try {
      const sid = await ensureDraft();
      const fd = new FormData();
      fd.append("file", file);
      const { data: asset } = await api.post(`/stories/${sid}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setClientLogoPath(asset.path);
    } catch (e) {
      setUploadError(e?.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await saveProgress();
      const sid = await ensureDraft();
      const { data: result } = await api.post(`/stories/${sid}/submit`);
      setReward(result);
      cjReact("Story submitted!", {
        subtitle: result?.points_awarded_now ? `+${result.points_awarded_now} points landed. CJ will let your COO know.` : "CJ will let your COO know.",
        intensity: "normal",
        icon: "🚀",
      });
      await refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const checkStrength = async () => {
    setScoring(true);
    try {
      await saveProgress();
      const sid = await ensureDraft();
      const { data: result } = await api.post(`/stories/${sid}/strength`);
      setStrength(result);
    } catch (err) {
      alert(err?.response?.data?.detail || "Couldn't score this one");
    } finally {
      setScoring(false);
    }
  };

  const [suggestingTitle, setSuggestingTitle] = useState(false);
  const suggestTitle = async () => {
    setSuggestingTitle(true);
    try {
      await saveProgress();
      const sid = await ensureDraft();
      const { data: result } = await api.post(`/stories/${sid}/suggest-title`);
      if (result?.title) update({ title: result.title });
    } catch (err) {
      alert(err?.response?.data?.detail || "Couldn't draft a title");
    } finally {
      setSuggestingTitle(false);
    }
  };

  const stepKey = STEPS[step]?.key;
  const isReview = step === STEPS.length;
  const progress = Math.round(((step + (isReview ? 1 : 0)) / (STEPS.length + 1)) * 100);

  const stepInvalid = useMemo(() => {
    switch (stepKey) {
      case "industry":     return !data.industry;
      case "company_name": return !data.company_name?.trim();
      case "company_size": return !data.company_size;
      case "challenge":    return !data.challenge_text;
      case "trigger":      return !data.trigger_to_act;
      case "solution":     return !(data.true_colors_solution && data.true_colors_solution.length > 0);
      case "after":        return !data.after_text;
      case "consent":      return !data.consent_status;
      default:             return false; // optional steps
    }
  }, [stepKey, data]);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));
  const updateTags = (patch) => setData((d) => ({ ...d, tags: { ...d.tags, ...patch } }));
  const toggleProofPoint = (item) => setData((d) => {
    const cur = d.proof_points || [];
    return { ...d, proof_points: cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item] };
  });

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-6 pb-12">
      {/* progress bar */}
      <div className="sticky top-16 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 bg-[var(--tc-bg-soft)]/90 backdrop-blur py-3 mb-5">
        <div className="flex items-center justify-between mb-2">
          <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-900 flex items-center gap-1 text-sm font-bold" data-testid="capture-back-button">
            <ArrowLeft size={16} /> Exit
          </button>
          <div className="tc-eyebrow text-slate-500" data-testid="capture-progress-label">
            {isReview ? "Review" : `Step ${step + 1} of ${STEPS.length}`} · {progress}%
          </div>
          <button
            onClick={async () => { await saveProgress(); navigate("/"); }}
            data-testid="capture-save-exit"
            className="text-orange-600 hover:underline flex items-center gap-1 text-sm font-bold"
          >
            <Save size={14} /> Save & exit
          </button>
        </div>
        <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
          <motion.div className="h-full rounded-full" style={{ background: "#f68b1f" }} animate={{ width: `${progress}%` }} transition={{ type: "spring", stiffness: 120, damping: 22 }} />
        </div>
      </div>

      <AnimatePresence mode="wait">
        {!isReview ? (
          <motion.div
            key={stepKey}
            initial={{ x: 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -30, opacity: 0 }}
            transition={{ type: "spring", stiffness: 180, damping: 22 }}
            className="tc-card p-6 sm:p-8"
            data-testid={`capture-step-${stepKey}`}
          >
            <h1 className="font-display text-3xl sm:text-4xl text-tc-ink leading-tight">{STEPS[step].title}</h1>
            <p className="text-slate-500 mt-1.5 text-sm sm:text-base">{STEPS[step].subtitle}</p>

            <div className="mt-7 space-y-4">
              {stepKey === "industry" && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {INDUSTRIES.map((i) => (
                    <button
                      key={i}
                      onClick={() => update({ industry: i, tags: { ...data.tags, industry: i } })}
                      data-testid={`pick-industry-${i.toLowerCase().replace(/\s/g, "-")}`}
                      className={`h-12 rounded-xl border-2 text-sm font-bold transition-all ${data.industry === i ? "border-orange-500 bg-orange-50 text-orange-900" : "border-slate-200 hover:border-slate-300"}`}
                    >
                      {i}
                    </button>
                  ))}
                </div>
              )}

              {stepKey === "company_name" && (
                <>
                  <input
                    value={data.company_name || ""}
                    onChange={(e) => update({ company_name: e.target.value })}
                    placeholder="e.g. Cleveland Clinic"
                    maxLength={120}
                    autoFocus
                    data-testid="capture-company-name"
                    className="w-full h-14 px-4 rounded-2xl border-2 border-slate-200 focus:border-orange-500 outline-none text-base font-bold text-tc-ink bg-white"
                  />
                  <p className="text-xs text-slate-500 mt-1">For internal tracking. You'll choose later whether to share the name publicly or keep it anonymous.</p>
                </>
              )}

              {stepKey === "company_size" && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {COMPANY_SIZES.map((s) => (
                    <button
                      key={s}
                      onClick={() => update({ company_size: s, tags: { ...data.tags, company_size: s } })}
                      data-testid={`pick-size-${s.replace(/[^a-z0-9]/gi, "")}`}
                      className={`h-12 rounded-xl border-2 text-sm font-bold transition-all ${data.company_size === s ? "border-blue-500 bg-blue-50 text-blue-900" : "border-slate-200 hover:border-slate-300"}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {stepKey === "challenge" && (
                <>
                  <textarea
                    value={data.challenge_text}
                    onChange={(e) => update({ challenge_text: e.target.value })}
                    placeholder={"Example: A 60-person manufacturing leadership team had communication breakdowns across departments. Two missed product launches in Q3 cost the company $400K. Engineering and Operations were not aligned and trust was low after the second miss."}
                    data-testid="challenge-textarea"
                    className="w-full min-h-[140px] rounded-2xl border-2 border-slate-200 focus:border-orange-500 outline-none p-4 text-base leading-relaxed"
                  />
                  <p className="text-xs text-slate-400 leading-snug mt-1">Stories with a specific number convert prospects roughly 3x better than vague ones. Try to include team size, dollar impact, or missed deadlines.</p>
                  <div>
                    <div className="tc-eyebrow text-slate-500 mb-2">Categories (pick any)</div>
                    <div className="flex flex-wrap gap-2">
                      {CHALLENGE_CATEGORIES.map((c) => {
                        const active = data.challenge_categories.includes(c);
                        return (
                          <button
                            key={c}
                            onClick={() => {
                              const cats = active ? data.challenge_categories.filter((x) => x !== c) : [...data.challenge_categories, c];
                              update({ challenge_categories: cats, tags: { ...data.tags, challenge_type: cats[0] || "" } });
                            }}
                            data-testid={`challenge-cat-${c.toLowerCase().replace(/\s/g, "-")}`}
                            className={`tc-pill border-2 ${active ? "border-orange-500 bg-orange-50 text-orange-900" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}
                          >
                            {active && <Check size={12} />} {c}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {stepKey === "trigger" && (
                <>
                  <textarea
                    value={data.trigger_to_act}
                    onChange={(e) => update({ trigger_to_act: e.target.value })}
                    placeholder={"Example: After two senior leaders quit in the same month, the new COO told the board he needed every department head aligned within 90 days or he would resign too."}
                    data-testid="trigger-textarea"
                    className="w-full min-h-[140px] rounded-2xl border-2 border-slate-200 focus:border-orange-500 outline-none p-4 text-base leading-relaxed"
                  />
                  <p className="text-xs text-slate-400 leading-snug mt-1">A clear catalyst makes the story land. Was there a board meeting, a walkout, a missed number, a leadership change?</p>
                </>
              )}

              {stepKey === "solution" && (
                <div className="grid sm:grid-cols-2 gap-2">
                  {TRUE_COLORS_SOLUTIONS.map((sol) => {
                    const selected = (data.true_colors_solution || []).includes(sol);
                    return (
                      <button
                        key={sol}
                        onClick={() => {
                          const cur = data.true_colors_solution || [];
                          const next = selected ? cur.filter((x) => x !== sol) : [...cur, sol];
                          update({ true_colors_solution: next });
                        }}
                        data-testid={`pick-solution-${sol.toLowerCase().replace(/[^a-z0-9]/gi, "-")}`}
                        className={`text-left h-auto p-4 rounded-2xl border-2 text-sm font-bold transition-all flex items-start gap-2.5 ${selected ? "border-green-500 bg-green-50 text-green-900" : "border-slate-200 hover:border-slate-300"}`}
                      >
                        <span
                          className={`mt-0.5 w-5 h-5 rounded-md border-2 shrink-0 flex items-center justify-center ${selected ? "bg-green-500 border-green-500" : "border-slate-300"}`}
                        >
                          {selected && <Check size={14} className="text-white" />}
                        </span>
                        <span className="leading-snug">{sol}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {stepKey === "engagement" && (
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <div className="tc-eyebrow text-slate-500 mb-1">People trained</div>
                      <input
                        value={data.people_trained}
                        onChange={(e) => update({ people_trained: e.target.value })}
                        placeholder="e.g. ~120 across 3 cohorts"
                        data-testid="capture-people-trained"
                        className="w-full h-12 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm"
                      />
                    </div>
                    <div>
                      <div className="tc-eyebrow text-slate-500 mb-1">When</div>
                      <input
                        value={data.training_date}
                        onChange={(e) => update({ training_date: e.target.value })}
                        placeholder="e.g. March 2025"
                        data-testid="capture-training-date"
                        className="w-full h-12 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <div className="tc-eyebrow text-slate-500 mb-1">Engagement type</div>
                      <input
                        value={data.engagement_type}
                        onChange={(e) => update({ engagement_type: e.target.value })}
                        placeholder="e.g. 2-day Train-the-Trainer / Half-day workshop / 4-week leadership series"
                        data-testid="capture-engagement-type"
                        className="w-full h-12 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <div className="tc-eyebrow text-slate-500 mb-1">Format</div>
                      <div className="grid grid-cols-3 gap-2">
                        {["In-person", "Virtual", "Hybrid"].map((fmt) => (
                          <button
                            key={fmt}
                            type="button"
                            onClick={() => update({ delivery_format: data.delivery_format === fmt ? "" : fmt })}
                            data-testid={`capture-format-${fmt.toLowerCase()}`}
                            className={`h-12 rounded-xl border-2 text-sm font-bold transition-all ${data.delivery_format === fmt ? "border-blue-500 bg-blue-50 text-blue-900" : "border-slate-200 hover:border-slate-300"}`}
                          >
                            {fmt}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="sm:col-span-2 mt-2 pt-4 border-t border-slate-100">
                      <div className="tc-eyebrow text-orange-600 mb-1">Client contact (for Robert's thank-you)</div>
                      <p className="text-xs text-slate-500 mb-2">If you have it: who at the client should Robert thank, and at what email? You can leave blank if you'd rather not.</p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <input
                          value={data.client_contact_name}
                          onChange={(e) => update({ client_contact_name: e.target.value })}
                          placeholder="Client contact name"
                          data-testid="capture-client-contact-name"
                          className="w-full h-12 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm"
                        />
                        <input
                          type="email"
                          value={data.client_contact_email}
                          onChange={(e) => update({ client_contact_email: e.target.value })}
                          placeholder="Client contact email"
                          data-testid="capture-client-contact-email"
                          className="w-full h-12 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-slate-400">All optional. Skip whatever you don't know. You can always add details later from the story page.</p>
                </div>
              )}

              {stepKey === "after" && (
                <>
                  <textarea
                    value={data.after_text}
                    onChange={(e) => update({ after_text: e.target.value })}
                    placeholder={"Example: Within 90 days, weekly cross-departmental syncs launched. Leaders reported 30% fewer escalations to the COO. The next product launch hit its date. The COO said the team is now talking in True Colors language during heated moments instead of shutting down."}
                    data-testid="after-textarea"
                    className="w-full min-h-[160px] rounded-2xl border-2 border-slate-200 focus:border-orange-500 outline-none p-4 text-base leading-relaxed"
                  />
                  <p className="text-xs text-slate-400 leading-snug mt-1">Specifics win. Try a number, a behavior change, or a phrase a leader actually used. Avoid "well received" or "went great".</p>
                </>
              )}

              {stepKey === "outcomes" && (
                <>
                  <div className="tc-pill bg-yellow-50 text-yellow-800 mb-1"><Sparkles size={12} /> +50 if you tick anything</div>
                  <div className="grid gap-2">
                    {PROOF_POINTS.map((item) => {
                      const active = (data.proof_points || []).includes(item);
                      const testid = `proof-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`;
                      return (
                        <button
                          key={item}
                          onClick={() => toggleProofPoint(item)}
                          data-testid={testid}
                          className={`text-left p-4 rounded-2xl border-2 transition-all flex items-start gap-3 ${active ? "border-yellow-500 bg-yellow-50" : "border-slate-200 hover:border-slate-300"}`}
                        >
                          <span
                            className={`mt-0.5 w-5 h-5 rounded-md border-2 shrink-0 flex items-center justify-center ${active ? "bg-yellow-500 border-yellow-500" : "border-slate-300"}`}
                          >
                            {active && <Check size={14} className="text-white" />}
                          </span>
                          <span className="text-sm sm:text-base font-bold leading-snug">{item}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {stepKey === "quote" && (
                <>
                  <div className="tc-pill bg-yellow-50 text-yellow-800 mb-1"><Sparkles size={12} /> +25 bonus</div>
                  <textarea
                    value={data.client_quote}
                    onChange={(e) => update({ client_quote: e.target.value })}
                    placeholder={`Example: "This is the first training that actually changed how our leaders behave in meetings. We pulled in a missed launch date because of it." Director of People, Acme`}
                    data-testid="quote-textarea"
                    className="w-full min-h-[120px] rounded-2xl border-2 border-slate-200 focus:border-yellow-500 outline-none p-4 text-base leading-relaxed italic"
                  />
                  <p className="text-xs text-slate-400 leading-snug mt-1">Even one line from a champion makes the story sticky. Ask in your follow-up: "Mind if I quote you on what changed?"</p>
                </>
              )}

              {stepKey === "media" && (
                <>
                  <div className="tc-pill bg-yellow-50 text-yellow-800 mb-1"><Sparkles size={12} /> +25 bonus</div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="media-upload-button"
                    className="w-full h-32 rounded-2xl border-2 border-dashed border-slate-300 hover:border-orange-400 hover:bg-orange-50 transition-all flex flex-col items-center justify-center gap-1 text-slate-500"
                  >
                    {uploading ? <span className="font-bold">Uploading…</span> : (
                      <>
                        <Upload size={22} />
                        <span className="font-bold">Tap to upload photo or video</span>
                        <span className="text-xs">JPG/PNG/WEBP or MP4 · up to 25MB</span>
                      </>
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    onChange={(e) => handleMediaUpload(e.target.files?.[0])}
                    className="hidden"
                    data-testid="media-file-input"
                  />
                  {uploadError && <div className="text-sm text-red-600">{uploadError}</div>}
                  {media.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                      {media.map((m, i) => (
                        <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100" data-testid={`media-preview-${i}`}>
                          {m.kind === "image" ? (
                            <img src={fileUrl(m.path)} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center text-slate-500">
                              <Film size={28} />
                              <span className="text-xs font-bold mt-1">Video</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {stepKey === "consent" && (
                <div className="space-y-2">
                  {CONSENT_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      onClick={() => update({ consent_status: o.value, follow_up_reminder: o.value === "not_yet_asked" })}
                      data-testid={`consent-${o.value}`}
                      className={`w-full text-left p-4 rounded-2xl border-2 transition-all ${data.consent_status === o.value ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                    >
                      <div className="font-bold">{o.label}</div>
                      {o.value === "not_yet_asked" && (
                        <div className="text-xs text-slate-500 mt-0.5">We'll add a follow-up reminder to your list.</div>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {stepKey === "tags" && (
                <div className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <label className="block">
                      <span className="tc-eyebrow text-slate-500">Outcome category</span>
                      <select
                        value={data.tags.outcome_category || ""}
                        onChange={(e) => updateTags({ outcome_category: e.target.value })}
                        data-testid="tag-outcome-category"
                        className="mt-1 w-full h-12 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm font-medium"
                      >
                        <option value="">Choose one…</option>
                        {OUTCOME_CATEGORIES.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="tc-eyebrow text-slate-500">Facilitator (optional)</span>
                      <input
                        value={data.facilitator || ""}
                        onChange={(e) => update({ facilitator: e.target.value })}
                        placeholder="Who delivered the session?"
                        data-testid="tag-facilitator"
                        className="mt-1 w-full h-12 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="tc-eyebrow text-slate-500">Country</span>
                      <select
                        value={data.country || ""}
                        onChange={(e) => update({ country: e.target.value, state: e.target.value === "United States" ? data.state : "" })}
                        data-testid="tag-country"
                        className="mt-1 w-full h-12 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm"
                      >
                        <option value="">Choose…</option>
                        {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="tc-eyebrow text-slate-500">{data.country === "United States" ? "State" : "State / Region"}</span>
                      {data.country === "United States" ? (
                        <select
                          value={data.state || ""}
                          onChange={(e) => update({ state: e.target.value })}
                          data-testid="tag-state"
                          className="mt-1 w-full h-12 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm"
                        >
                          <option value="">Choose…</option>
                          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      ) : (
                        <input
                          value={data.state || ""}
                          onChange={(e) => update({ state: e.target.value })}
                          placeholder="Region (optional)"
                          data-testid="tag-state"
                          className="mt-1 w-full h-12 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm"
                        />
                      )}
                    </label>
                    <label className="block sm:col-span-2">
                      <span className="tc-eyebrow text-slate-500">Future CRM client ID (optional)</span>
                      <input
                        value={data.client_id || ""}
                        onChange={(e) => update({ client_id: e.target.value })}
                        placeholder="e.g. SF-00012345"
                        data-testid="tag-client-id"
                        className="mt-1 w-full h-12 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm"
                      />
                    </label>
                  </div>

                  {/* Optional client logo */}
                  <div>
                    <div className="tc-eyebrow text-slate-500 mb-2">Client logo (optional)</div>
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      data-testid="logo-upload-button"
                      className="w-full h-24 rounded-2xl border-2 border-dashed border-slate-300 hover:border-orange-400 hover:bg-orange-50 transition-all flex items-center justify-center gap-2 text-slate-500"
                    >
                      {uploadingLogo ? <span className="font-bold">Uploading…</span> : clientLogoPath ? (
                        <>
                          <img src={fileUrl(clientLogoPath)} alt="Client logo" className="h-14 w-14 object-contain bg-white rounded-lg border border-slate-200 p-1" />
                          <span className="font-bold text-tc-ink">Logo attached, tap to replace</span>
                        </>
                      ) : (
                        <>
                          <ImageIcon size={18} />
                          <span className="font-bold">Tap to upload client logo</span>
                        </>
                      )}
                    </button>
                    <input ref={logoInputRef} type="file" accept="image/*" onChange={(e) => handleLogoUpload(e.target.files?.[0])} className="hidden" data-testid="logo-file-input" />
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-8">
              <button
                onClick={back}
                disabled={step === 0}
                data-testid="capture-prev-button"
                className="h-12 px-5 rounded-2xl border border-slate-200 font-bold text-slate-700 disabled:opacity-30 flex items-center gap-1"
              >
                <ArrowLeft size={16} /> Back
              </button>
              <button
                onClick={next}
                disabled={stepInvalid || saving}
                data-testid="capture-next-button"
                className="h-12 px-6 rounded-2xl text-white font-extrabold flex items-center gap-1 shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-40"
                style={{ background: stepInvalid ? "#94A3B8" : "#083050" }}
              >
                {step === STEPS.length - 1 ? "Review" : "Next"} <ArrowRight size={16} />
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="review"
            initial={{ x: 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -30, opacity: 0 }}
            className="tc-card p-6 sm:p-8"
            data-testid="capture-review"
          >
            <div className="tc-eyebrow text-orange-600">Last look</div>
            <h1 className="font-display text-3xl text-tc-ink mt-1">Ready to ship it?</h1>
            <p className="text-slate-500 text-sm mt-1">After you submit, your COO reviews & approves. Points hit your account now.</p>

            {/* Story title — short headline used everywhere this story is shown */}
            <div className="mt-6 p-4 rounded-2xl border-2 border-dashed border-orange-200 bg-orange-50/40" data-testid="capture-title-block">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="tc-eyebrow text-orange-600">Story title</div>
                  <p className="text-[11px] text-slate-500 mt-0.5">A short headline (max ~60 chars). CJ can draft one.</p>
                </div>
                <button
                  onClick={suggestTitle}
                  disabled={suggestingTitle || !data.challenge_text}
                  data-testid="capture-suggest-title"
                  className="h-9 px-3 rounded-xl text-white font-bold text-xs inline-flex items-center gap-1.5 shadow-sm hover:-translate-y-0.5 transition-all disabled:opacity-50"
                  style={{ background: "#f68b1f" }}
                >
                  {suggestingTitle ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                  {suggestingTitle ? "CJ is drafting..." : (data.title ? "Re-draft" : "Suggest with CJ")}
                </button>
              </div>
              <input
                value={data.title || ""}
                onChange={(e) => update({ title: e.target.value })}
                placeholder="e.g. Healthcare team rebuilds after merger"
                maxLength={80}
                data-testid="capture-title-input"
                className="mt-3 w-full h-11 px-3 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none text-sm font-bold text-tc-ink bg-white"
              />
            </div>

            <div className="mt-6 space-y-4">
              <ReviewRow label="Industry · Size" value={`${data.industry} · ${data.company_size}`} />
              <ReviewRow label="Challenge" value={data.challenge_text} chips={data.challenge_categories} />
              <ReviewRow label="Trigger" value={data.trigger_to_act} />
              <ReviewRow label="Solution" value={(data.true_colors_solution || []).join(", ")} chips={data.true_colors_solution} />
              <ReviewRow label="After" value={data.after_text} />
              {(data.proof_points && data.proof_points.length > 0) && (
                <ReviewRow label="Proof points" value={`${data.proof_points.length} ticked`} chips={data.proof_points} />
              )}
              {data.client_quote && <ReviewRow label="Quote" value={`"${data.client_quote}"`} />}
              {media.length > 0 && <ReviewRow label="Media" value={`${media.length} attachment${media.length > 1 ? "s" : ""}`} />}
              <ReviewRow label="Consent" value={CONSENT_OPTIONS.find((o) => o.value === data.consent_status)?.label || ","} />
            </div>

            {/* Story Strength meter */}
            <StrengthMeter strength={strength} scoring={scoring} onCheck={checkStrength} />

            <div className="flex items-center justify-between mt-8">
              <button onClick={back} className="h-12 px-5 rounded-2xl border border-slate-200 font-bold flex items-center gap-1" data-testid="review-back">
                <ArrowLeft size={16} /> Edit
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="submit-story-button"
                className="h-12 px-6 rounded-2xl text-white font-extrabold flex items-center gap-2 shadow-md hover:-translate-y-0.5 transition-all"
                style={{ background: "#f68b1f" }}
              >
                <Send size={16} /> {submitting ? "Submitting…" : "Submit & earn points"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <RewardOverlay
        open={!!reward}
        onClose={() => { setReward(null); navigate("/"); }}
        result={reward}
        archetypeKey={user?.archetype}
      />
    </div>
  );
}

function ReviewRow({ label, value, chips }) {
  if (!value) return null;
  return (
    <div className="border-b border-slate-100 pb-3">
      <div className="tc-eyebrow text-slate-400">{label}</div>
      <div className="text-sm sm:text-base text-tc-ink mt-1 leading-relaxed">{value}</div>
      {chips && chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {chips.map((c) => <span key={c} className="tc-pill bg-slate-100 text-slate-700">{c}</span>)}
        </div>
      )}
    </div>
  );
}


function StrengthMeter({ strength, scoring, onCheck }) {
  if (!strength) {
    return (
      <div className="mt-6 p-5 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/60 text-center">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Wand2 size={18} className="text-orange-500" />
          <div className="font-display text-lg text-tc-ink">Story Strength check</div>
        </div>
        <p className="text-sm text-slate-500 leading-snug max-w-md mx-auto">
          Want CJ to tell you how strong this story is before you submit? She'll score it on six factors and tell you what's missing.
        </p>
        <button
          onClick={onCheck}
          disabled={scoring}
          data-testid="strength-check"
          className="mt-3 h-11 px-5 rounded-xl text-white font-bold text-sm inline-flex items-center gap-2 shadow-sm hover:-translate-y-0.5 transition-all disabled:opacity-60"
          style={{ background: "#f68b1f" }}
        >
          {scoring ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
          {scoring ? "CJ is reading..." : "Check Story Strength"}
        </button>
      </div>
    );
  }

  const color = strength.label === "Strong" ? "#7ac143" : strength.label === "Solid" ? "#0091be" : "#f68b1f";
  return (
    <div className="mt-6 p-5 rounded-2xl border-2 bg-white" style={{ borderColor: color }} data-testid="strength-result">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-display text-2xl shadow-sm" style={{ background: color }}>
            {strength.score}
          </div>
          <div>
            <div className="font-display text-xl text-tc-ink leading-tight">{strength.label} story</div>
            <div className="text-xs text-slate-500">Scored on {strength.score_max} factors</div>
          </div>
        </div>
        <button
          onClick={onCheck}
          disabled={scoring}
          data-testid="strength-recheck"
          className="text-xs font-bold text-slate-500 hover:text-tc-ink inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-slate-200 hover:border-slate-300"
        >
          {scoring ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
          Re-check
        </button>
      </div>

      {strength.summary && (
        <div className="mt-4 p-3 rounded-xl bg-slate-50 text-sm text-slate-700 leading-relaxed italic">"{strength.summary}"</div>
      )}

      <div className="mt-4 grid sm:grid-cols-2 gap-2">
        {(strength.factors || []).map((f) => (
          <div key={f.key} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-slate-50/70">
            {f.present ? (
              <Check size={16} className="text-green-600 mt-0.5 shrink-0" />
            ) : (
              <XIcon size={16} className="text-slate-400 mt-0.5 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="text-xs font-bold text-tc-ink">{f.label}</div>
              {!f.present && f.hint && <div className="text-[11px] text-slate-500 leading-snug mt-0.5">{f.hint}</div>}
            </div>
          </div>
        ))}
      </div>

      {(strength.suggestions || []).length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1.5">CJ's suggestions</div>
          <ul className="text-sm text-slate-700 space-y-1.5">
            {strength.suggestions.map((s, i) => (
              <li key={i} className="flex items-start gap-2"><Sparkles size={12} className="text-orange-500 mt-1 shrink-0" /><span>{s}</span></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
